import Handlebars from 'handlebars';
import store from '@/app/store/store';
import { templatesApi } from '@/features/templates/templatesApi';
import { formatDataForScopeOfAdvice } from '@/features/templates/helper';
import {
	applyParagraphStylesToHtml,
	applyLegacyScopeTableStyles,
} from '@/components/Drawers/AddUnlayerEmailDrawer/tableStyleFormatter';

const getBlockHtmlByType = async ({ type }) => {
	const blockResponse = await store.dispatch(
		templatesApi.endpoints.getEmailTemplateBlockByType.initiate(
			{
				type,
			},
			{ subscribe: false },
		),
	).unwrap();

	const block = (Array.isArray(blockResponse) ? blockResponse : [blockResponse]).find(
		(entry) => entry?.type === type,
	);
	return block?.html?.toString?.() ?? '';
};

const compileBlockHtml = ({ html, templateData }) => {
	const compiled = Handlebars.compile(html.replace(/>\s+</g, '><').replace(/\n/g, ''));
	return compiled(templateData ?? {});
};

const getScopeOfAdviceTemplateData = ({
	scopeOfAdviceClientEnvelope,
	scopeOfAdviceDealContext,
	userDetails,
	isMarble,
	classesOfRisk,
}) =>
	formatDataForScopeOfAdvice({
		client: scopeOfAdviceClientEnvelope,
		dealRow: scopeOfAdviceDealContext,
		userData: userDetails,
		isMarble,
		classesOfRisk,
	});

/**
 * Returns the HTML string for the needs analysis paraphraph to be injected into the email template.
 * The content of the paragraph changes based on whether a full needs analysis was conducted or not,
 * and includes a link to the brokerage disclosure statement if a full needs analysis was not conducted.
 */
const buildNeedsAnalysisParagraphHtml = ({ disclosureUrl, fullNeedsAnalysis }) => {
	let firstParagraph =
		'We conducted a full needs analysis to identify and understand all risks relevant to the business.';
	if (!fullNeedsAnalysis) {
		const safeDisclosureUrl =
			typeof disclosureUrl === 'string' && disclosureUrl.trim().length > 0
				? disclosureUrl.trim()
				: '{{brokerage.disclosureUrl}}';
		firstParagraph = `By only wanting to discuss the products noted above, we couldn't conduct a full needs analysis. The disadvantages of this are noted in our disclosure statement <a href="${safeDisclosureUrl}">here</a>.`;
	}

	return `
		<div class="scope-of-advice-paragraphs">
			<p>${firstParagraph}</p>
			<p>If you'd like to expand our focus to add any more of the above risk areas, please let me know, and I'll adjust our scope as needed.</p>
		</div>
	`;
};

export const buildScopeOfAdviceTableHtml = async ({
	scopeOfAdviceDealContext,
	scopeOfAdviceClientEnvelope,
	classesOfRisk,
	userDetails,
	isMarble,
}) => {
	const html = await getBlockHtmlByType({
		type: 'deal.soaTable',
	});
	if (html.length === 0) return '';

	const scopeOfAdviceData = getScopeOfAdviceTemplateData({
		scopeOfAdviceClientEnvelope,
		scopeOfAdviceDealContext,
		userDetails,
		isMarble,
		classesOfRisk,
	});

	const compiledHtml = compileBlockHtml({
		html,
		templateData: scopeOfAdviceData,
	});
	return applyLegacyScopeTableStyles(compiledHtml);
};

export const buildNeedsAnalysisTableHtml = async ({
	scopeOfAdviceClientEnvelope,
	scopeOfAdviceDealContext,
	classesOfRisk,
	userDetails,
	isMarble,
}) => {
	const html = await getBlockHtmlByType({
		type: 'deal.needsAnalysisTable',
	});
	if (html.length === 0) return '';

	const scopeOfAdviceData = getScopeOfAdviceTemplateData({
		scopeOfAdviceClientEnvelope,
		scopeOfAdviceDealContext,
		userDetails,
		isMarble,
		classesOfRisk,
	});

	const compiledHtml = compileBlockHtml({
		html,
		templateData: scopeOfAdviceData,
	});
	return applyLegacyScopeTableStyles(compiledHtml);
};

/**
 * Builds the HTML for the needs analysis paragraph to be injected into the email template.
 * The HTML contains:
 * - the wording for the paragraph, which changes based on whether a full needs analysis was conducted or not
 * - a link to the brokerage disclosure statement if a full needs analysis was not conducted, with a fallback to the token if the URL is not available
 * - inline styling
 */
export const buildNeedsAnalysisParagraph = ({
	scopeOfAdviceClientEnvelope,
	scopeOfAdviceDealContext,
	classesOfRisk,
	userDetails,
	isMarble,
}) => {
	if (!isMarble) return '';

	const scopeOfAdviceData = getScopeOfAdviceTemplateData({
		scopeOfAdviceClientEnvelope,
		scopeOfAdviceDealContext,
		userDetails,
		isMarble,
		classesOfRisk,
	});
	const disclosureUrl = scopeOfAdviceData?.brokerage?.disclosureUrl ?? '';

	const html = buildNeedsAnalysisParagraphHtml({
		disclosureUrl,
		fullNeedsAnalysis: Boolean(scopeOfAdviceData?.fullNeedsAnalysis),
	});

	return applyParagraphStylesToHtml(html);
};
